<template>
	<section
		:class="['Accordion', { 'isCollapsed': isCollapsed }]"
	>
		<div class="Header">
			<span class="Title">{{ title }}</span>
			<div class="ToggleArea">
				<button
					v-if="showClear"
					class="Clear"
					aria-label="Clear Accordion"
					@click="emit('clear')"
				>
					Clear
				</button>
				<button
					class="Toggle"
					aria-label="Toggle Accordion"
					@click="toggleCollapsed"
				>
					<i
						class="lnr-chevron-up"
						aria-hidden="true"
					></i>
				</button>
			</div>
		</div>

		<!--
			NOTE: this nested is needed to
			make the wanted transition work correctly keeping the standard padding for this component
		-->
		<div class="Body">
			<div class="BodyOverflow">
				<div class="BodyContent">
					<slot name="content" />
				</div>
			</div>
		</div>

		<div class="Footer">
			<slot name="footer" />
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, type Ref } from 'vue';

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	showClear: {
		type: Boolean,
		default: true,
	},
	initiallyCollapsed: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits<{
	(e: 'clear'): void;
	(e: 'collapseChange', isCollapsed: boolean): void;
}>();

const isCollapsed: Ref<boolean> = ref(props.initiallyCollapsed);

const toggleCollapsed = () => {
	isCollapsed.value = !isCollapsed.value;
	emit('collapseChange', isCollapsed.value);
};
</script>

<style scoped lang="scss">
	.Accordion {
		height: fit-content;
		background: $White;
		border-bottom: 1px solid $Grey-300;

		@include PaddingAreas(1.5, 1);

		@include Breakpoint(Small, Medium) {
			&.isCollapsed {
				border: none;
			}
		}

		@include Breakpoint(Large, ExtraLarge) {
			&:only-of-type {
				border-top: 1px solid $Grey-300;
				border-bottom: 1px solid $Grey-300;
			}
			&:first-child:not(:only-of-type) {
				border-top: none;
			}

			&:last-child:not(:only-of-type) {
				border-bottom: none;
			}
		}

		.Header {
			display: flex;
			justify-content: space-between;
			align-items: center;

			.Title {
				color: $Grey-900;
				user-select: none;

				@include TextStyle(HeadingExtraSmall);
			}

			.ToggleArea {
				display: flex;
				align-items: center;
				gap: 1rem;
				user-select: none;

				.Clear {
					color: $Blue-700;
					cursor: pointer;

					@include TextStyle(BodySmallRegular);
				}

				.Toggle {
					min-width: 1rem;
					min-height: 1.375rem;
					font-size: 1rem;
					color: $Grey-900;
					cursor: pointer;
					transition: transform 0.3s ease;
					display: inline-block;
				}

				.Toggle, .Clear {
					background: none;
					border: none;
					padding: 0;
				}
			}
		}

		.Body {
			display: grid;
			grid-template-rows: 1fr;
			transition: grid-template-rows 0.3s ease;

			.BodyOverflow {
			overflow: hidden;

				.BodyContent {
					display: flex;
					justify-content: center;
					align-items: center;
					@include PaddingAreas(1.5, 0, 0, 0);
				}
			}
		}


		.Footer {
			display: none;
		}

		&.isCollapsed {
			.Toggle {
				transform: rotate(180deg);
			}

			.Body {
				grid-template-rows: 0fr;
			}

			.Footer {
				display: block;
			}
		}
	}
</style>
